import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import apiClient from "../../lib/api";
import {
  ChartLineUp, Users, Trophy, CurrencyDollar, Target, Clock,
  CaretRight, CaretDown, Warning, CheckCircle, XCircle, Plus, ArrowClockwise,
  ShieldSlash, Trash, X, Bell, Gift, Power, CreditCard, Wrench, Lightning, EnvelopeSimple,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  PieChart, Pie, Cell, Tooltip, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";
import { format } from "date-fns";
import { toast } from "sonner";
import PendingActivationsCard from "../../components/PendingActivationsCard";
import StripeLookupCard from "../../components/StripeLookupCard";

const PLAN_LABELS = { weekly: "Semanal", monthly: "Mensual", vip: "VIP", premium: "Premium", free: "Free" };

export default function AdminPanel() {
  const [stats, setStats] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suspending, setSuspending] = useState(false);

  // Cleanup modal state
  const [showCleanupConfirm, setShowCleanupConfirm] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [cleanupReport, setCleanupReport] = useState(null);

  // Navegación por pestañas
  const [tab, setTab] = useState("resumen");
  const [confirmToggle, setConfirmToggle] = useState(null);
  const [cleanupArmed, setCleanupArmed] = useState(false);

  // Email broadcast state
  const [emailAudience, setEmailAudience] = useState("inactive");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null); // { target_count, stats }

  // Trials toggle state
  const [trialsEnabled, setTrialsEnabled] = useState(true);
  const [togglingTrials, setTogglingTrials] = useState(false);
  const [requireCard, setRequireCard] = useState(false);
  const [togglingRequireCard, setTogglingRequireCard] = useState(false);

  // Push broadcast state
  const [pushTitle, setPushTitle] = useState("");
  const [pushMessage, setPushMessage] = useState("");
  const [pushUrl, setPushUrl] = useState("/jugadas");
  const [sending, setSending] = useState(false);
  const [pushStats, setPushStats] = useState(null);

  const loadPushStats = useCallback(async () => {
    try {
      const r = await apiClient.get("/admin/push/stats");
      setPushStats(r.data);
    } catch { /* silencioso */ }
  }, []);

  const sendBroadcast = async () => {
    if (!pushTitle.trim() || !pushMessage.trim()) {
      toast.error("Título y mensaje son requeridos");
      return;
    }
    setSending(true);
    try {
      const r = await apiClient.post("/admin/push/broadcast", {
        title: pushTitle.trim(),
        message: pushMessage.trim(),
        url: pushUrl.trim() || "/jugadas",
      });
      toast.success(`Notificación enviada a ${r.data.sent} / ${r.data.total} suscriptores`);
      setPushTitle("");
      setPushMessage("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al enviar notificación");
    }
    setSending(false);
  };

  const previewEmailBroadcast = async () => {
    try {
      const r = await apiClient.post("/admin/email-broadcast", {
        audience: emailAudience,
        dry_run: true,
      });
      setEmailPreview(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al obtener vista previa");
    }
  };

  const sendEmailBroadcast = async () => {
    if (!emailPreview) {
      toast.error("Primero haz clic en 'Ver destinatarios'");
      return;
    }
    if (emailPreview.target_count === 0) {
      toast.error("No hay usuarios en esa audiencia");
      return;
    }
    const confirmed = window.confirm(
      `¿Enviar correo a ${emailPreview.target_count} usuarios?\n\nEsta acción no se puede deshacer.`
    );
    if (!confirmed) return;
    setEmailSending(true);
    try {
      const r = await apiClient.post("/admin/email-broadcast", {
        audience: emailAudience,
        subject: emailSubject.trim(),
        custom_message: emailMessage.trim(),
        dry_run: false,
      });
      toast.success(`Enviado a ${r.data.sent} de ${r.data.total} usuarios`);
      setEmailPreview(null);
      setEmailSubject("");
      setEmailMessage("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al enviar");
    }
    setEmailSending(false);
  };

  const load = useCallback(async () => {
    try {
      const [s, m, cfg] = await Promise.all([
        apiClient.get("/admin/stats/dashboard"),
        apiClient.get("/admin/stats/monthly"),
        apiClient.get("/admin/config"),
      ]);
      setStats(s.data);
      setMonthly(m.data.monthly || []);
      setTrialsEnabled(cfg.data?.trials_enabled !== false);
      setRequireCard(!!cfg.data?.require_card_for_trial);
    } catch (err) {
      console.error("[AdminPanel] load failed:", err?.response?.data || err.message);
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleTrials = async () => {
    const next = !trialsEnabled;
    setTogglingTrials(true);
    setConfirmToggle(null);
    try {
      await apiClient.put("/admin/config", { trials_enabled: next });
      setTrialsEnabled(next);
      toast.success(next
        ? "Free Trials ACTIVADOS — los nuevos registros recibirán días gratis"
        : "Free Trials PAUSADOS — los nuevos registros no recibirán días gratis");
    } catch (e) {
      toast.error("Error al actualizar la configuración");
    }
    setTogglingTrials(false);
  };

  const toggleRequireCard = async () => {
    const next = !requireCard;
    setTogglingRequireCard(true);
    setConfirmToggle(null);
    try {
      await apiClient.put("/admin/config", { require_card_for_trial: next });
      setRequireCard(next);
      toast.success(next
        ? "Tarjeta REQUERIDA — los nuevos registros deben proveer tarjeta para el trial"
        : "Tarjeta NO requerida — registros con trial sin tarjeta");
    } catch (e) {
      toast.error("Error al actualizar la configuración");
    }
    setTogglingRequireCard(false);
  };

  useEffect(() => { load(); loadPushStats(); }, [load, loadPushStats]);

  const runSuspension = async () => {
    setSuspending(true);
    try {
      const r = await apiClient.post("/admin/run-suspension");
      if (r.data.suspended > 0) {
        toast.success(`${r.data.suspended} cuenta(s) suspendidas automáticamente`);
      } else {
        toast.info("Sin cuentas pendientes de suspensión");
      }
      await load();
    } catch { toast.error("Error al ejecutar suspensión"); }
    setSuspending(false);
  };

  const runCleanup = async () => {
    setCleaning(true);
    setShowCleanupConfirm(false);
    try {
      const r = await apiClient.post("/admin/cleanup-test-data");
      setCleanupReport(r.data);
      toast.success(`Limpieza completada — ${r.data.total_deleted} elemento(s) eliminados`);
      await load();
    } catch {
      toast.error("Error al ejecutar limpieza");
    }
    setCleaning(false);
  };

  if (loading) return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-8 space-y-4">
      {[...Array(3)].map((_, i) => <div key={i} className="surface rounded-xl h-32 animate-pulse" />)}
    </div>
  );

  const wonPct = stats?.win_rate || 0;
  const lostPct = stats?.win_rate ? Math.max(0, 100 - stats.win_rate) : 0;
  const pieData = [
    { name: "Ganadas", value: wonPct, fill: "#10B981" },
    { name: "Perdidas", value: lostPct, fill: "#EF4444" },
  ].filter(d => d.value > 0);

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-8 pb-16 space-y-8">
{/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="text-[11px] text-yellow-400 tracking-[0.25em] uppercase font-bold mb-2">Panel Admin</div>
          <h1 className="text-display font-black text-3xl lg:text-5xl tracking-tighter leading-none">Control Room</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/admin/analisis/nuevo" className="btn-primary" data-testid="admin-create-pick-cta">
            <Plus weight="bold" className="w-4 h-4" /> Nueva jugada
          </Link>
          <button onClick={load} className="btn-secondary"><ArrowClockwise className="w-4 h-4" /></button>
        </div>
      </div>
      {/* Tabs de navegación */}
      <div className="flex gap-2 p-1 rounded-2xl" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="admin-tabs">
        {[
          { k: "resumen", l: "Resumen", icon: ChartLineUp },
          { k: "comunicacion", l: "Comunicación", icon: Bell },
          { k: "herramientas", l: "Herramientas", icon: Wrench },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`admin-tab-${t.k}`}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all ${tab === t.k ? "text-black" : "text-zinc-400 hover:text-white"}`}
            style={tab === t.k ? { background: "var(--gold)", boxShadow: "0 4px 18px rgba(212,175,55,0.35)" } : {}}>
            <t.icon weight="bold" className="w-4 h-4" />
            <span className="hidden sm:inline">{t.l}</span>
          </button>
        ))}
      </div>

      {tab === "resumen" && (
        <div className="space-y-8">
{/* Auto-suspension alert */}
      {stats?.pending_suspension > 0 && (
        <div data-testid="suspension-alert"
          className="flex items-center justify-between gap-4 p-4 rounded-xl border border-orange-500/25 bg-orange-500/8">
          <div className="flex items-center gap-3">
            <Warning weight="fill" className="w-5 h-5 text-orange-400 flex-shrink-0" />
            <div>
              <div className="font-bold text-orange-300 text-sm">
                {stats.pending_suspension} cuenta(s) pendiente(s) de suspensión automática
              </div>
              <div className="text-xs text-zinc-500">Suscripciones vencidas hace +7 días sin pagar</div>
            </div>
          </div>
          <button onClick={runSuspension} disabled={suspending}
            data-testid="run-suspension-btn"
            className="flex items-center gap-2 text-xs px-4 py-2 rounded-lg bg-orange-500/15 border border-orange-500/25 text-orange-300 hover:bg-orange-500/25 transition-all disabled:opacity-50 whitespace-nowrap">
            <ShieldSlash className="w-3.5 h-3.5" />
            {suspending ? "Ejecutando..." : "Suspender ahora"}
          </button>
        </div>
      )}{/* KPI Cards — 7 metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
        {[
          { label: "Con membresía",  value: stats?.active_paid_users ?? 0,            icon: Users,          color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-white/[0.06]", numColor: "text-white",                           testId: "stat-active-paid-users" },
          { label: "En trial",       value: stats?.trial_users ?? 0,                  icon: Clock,          color: "text-cyan-400",    bg: "bg-cyan-500/10",    border: "border-white/[0.06]", numColor: "text-white",                           testId: "stat-trial" },
          { label: "No renovaron",   value: stats?.not_renewed_users ?? 0,            icon: XCircle,        color: "text-orange-400",  bg: "bg-orange-500/10",  border: "border-white/[0.06]", numColor: (stats?.not_renewed_users ?? 0) > 0 ? "text-orange-400" : "text-white", testId: "stat-not-renewed" },
          { label: "Suspendidos",    value: stats?.suspended_users ?? 0,              icon: ShieldSlash,    color: "text-red-400",     bg: "bg-red-500/10",     border: (stats?.suspended_users ?? 0) > 0 ? "border-red-500/25" : "border-white/[0.06]", numColor: (stats?.suspended_users ?? 0) > 0 ? "text-red-400" : "text-white", testId: "stat-suspended" },
          { label: "Revenue mes",    value: `$${(stats?.revenue_month || 0).toFixed(0)}`, icon: CurrencyDollar, color: "text-amber-400",   bg: "bg-amber-500/10",  border: "border-amber-500/20",  numColor: "text-amber-400",                      testId: "stat-revenue-month" },
          { label: "Total Picks",    value: stats?.total_picks_display ?? stats?.total_picks ?? 0, icon: Trophy, color: "text-blue-400", bg: "bg-blue-500/10", border: "border-white/[0.06]", numColor: "text-white", testId: "stat-picks-month" },
          { label: "Win Rate",       value: `${stats?.win_rate ?? 0}%`,               icon: Target,         color: (stats?.win_rate ?? 0) >= 60 ? "text-emerald-400" : "text-amber-400", bg: (stats?.win_rate ?? 0) >= 60 ? "bg-emerald-500/10" : "bg-amber-500/10", border: "border-white/[0.06]", numColor: (stats?.win_rate ?? 0) >= 60 ? "text-emerald-400" : "text-amber-400", testId: "stat-win-rate" },
        ].map(s => (
          <div key={s.label} data-testid={s.testId}
            className={`rounded-2xl p-4 border ${s.border} hover:-translate-y-0.5 transition-transform`}
            style={{ background: "rgba(255,255,255,0.025)" }}>
            <div className={`${s.bg} w-8 h-8 rounded-lg flex items-center justify-center mb-3`}>
              <s.icon weight="duotone" className={`w-4 h-4 ${s.color}`} />
            </div>
            <div className={`number-ticker text-3xl font-black leading-none ${s.numColor}`}>{s.value}</div>
            <div className="text-[9px] text-zinc-400 uppercase tracking-widest font-bold mt-1.5">{s.label}</div>
          </div>
        ))}
      </div>
      {/* Comparativa revenue vs mes anterior */}
      {(() => {
        const revs = (monthly || []).map(m => m.revenue || 0);
        const cur = revs.length > 0 ? revs[revs.length - 1] : 0;
        const prev = revs.length > 1 ? revs[revs.length - 2] : 0;
        const diff = prev > 0 ? ((cur - prev) / prev) * 100 : (cur > 0 ? 100 : 0);
        const up = diff >= 0;
        return (
          <div className="flex items-center gap-1.5 mt-1.5">
            <span className={`text-[10px] font-black ${up ? "text-emerald-400" : "text-red-400"}`}>
              {up ? "▲" : "▼"} {Math.abs(diff).toFixed(0)}%
            </span>
            <span className="text-[9px] text-zinc-400 uppercase tracking-wider font-bold">vs mes anterior</span>
          </div>
        );
      })()}
{/* Charts row — CSS-only, mobile-friendly (no Recharts SVG que falla en iOS Safari) */}
      <div className="grid lg:grid-cols-3 gap-4">

        {/* Revenue meses — mini barras CSS */}
        <div className="surface rounded-xl p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-bold">Últimos 6 meses</div>
              <div className="text-base font-bold text-white leading-tight">Revenue</div>
            </div>
            <div className="text-right">
              <div className="text-xl font-black text-amber-400">${(stats?.revenue_total || 0).toFixed(0)}</div>
              <div className="text-[10px] text-zinc-500 uppercase">Total acumulado</div>
            </div>
          </div>
          {monthly.length > 0 ? (() => {
            const maxRev = Math.max(...monthly.map(m => m.revenue || 0), 1);
            return (
              <div className="flex items-end gap-1.5 h-16">
                {monthly.map((m, i) => {
                  const pct = Math.max(((m.revenue || 0) / maxRev) * 100, (m.revenue || 0) > 0 ? 8 : 2);
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <div className="text-[9px] font-bold text-amber-400">{(m.revenue || 0) > 0 ? `$${m.revenue}` : ""}</div>
                      <div className="w-full rounded-t-sm" style={{ height: `${pct}%`, background: (m.revenue || 0) > 0 ? "rgba(245,158,11,0.7)" : "rgba(255,255,255,0.06)", minHeight: 3 }} />
                      <div className="text-[9px] text-zinc-400 truncate max-w-full">{m.month_label}</div>
                    </div>
                  );
                })}
              </div>
            );
          })() : (
            <div className="flex items-center gap-2 py-3 text-xs text-zinc-400">
              <ChartLineUp className="w-4 h-4" />
              Sin movimiento registrado
            </div>
          )}
        </div>

        {/* Ganadas vs Perdidas — barra de progreso CSS */}
        <div className="surface rounded-xl p-5">
          <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-bold mb-1">Resultados</div>
          <div className="text-base font-bold text-white mb-4">Ganadas vs Perdidas</div>
          {pieData.length > 0 ? (
            <div className="space-y-3">
              {/* Win bar */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-zinc-400 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
                    Ganadas
                  </span>
                  <span className="text-sm font-black text-emerald-400">{wonPct.toFixed(1)}%</span>
                </div>
                <div className="h-2.5 rounded-full bg-white/[0.06] overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700" style={{ width: `${wonPct}%`, background: "linear-gradient(90deg, #10B981, #34D399)" }} />
                </div>
              </div>
              {/* Loss bar */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-zinc-400 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
                    Perdidas
                  </span>
                  <span className="text-sm font-black text-red-400">{lostPct.toFixed(1)}%</span>
                </div>
                <div className="h-2.5 rounded-full bg-white/[0.06] overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700" style={{ width: `${lostPct}%`, background: "linear-gradient(90deg, #EF4444, #F87171)" }} />
                </div>
              </div>
              {/* Total picks label */}
              <div className="text-[10px] text-zinc-400 text-center pt-1">
                {stats?.total_picks_display ?? stats?.total_picks ?? 0} picks totales
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 py-3 text-xs text-zinc-400">
              <Trophy className="w-4 h-4" />
              Sin picks resueltos
            </div>
          )}
        </div>
      </div>

      {/* Nuevos usuarios por mes — mini barras CSS */}
      <div className="surface rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-bold">Últimos 6 meses</div>
            <div className="text-base font-bold text-white leading-tight">Nuevos usuarios</div>
          </div>
          <div className="text-right">
            <div className="text-xl font-black text-blue-400">{(stats?.total_users || 0)}</div>
            <div className="text-[10px] text-zinc-500 uppercase">Total registrados</div>
          </div>
        </div>
        {monthly.length > 0 ? (() => {
          const maxU = Math.max(...monthly.map(m => m.new_users || 0), 1);
          return (
            <div className="flex items-end gap-1.5 h-16">
              {monthly.map((m, i) => {
                const pct = Math.max(((m.new_users || 0) / maxU) * 100, (m.new_users || 0) > 0 ? 8 : 2);
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div className="text-[9px] font-bold text-blue-400">{(m.new_users || 0) > 0 ? m.new_users : ""}</div>
                    <div className="w-full rounded-t-sm" style={{ height: `${pct}%`, background: (m.new_users || 0) > 0 ? "rgba(59,130,246,0.65)" : "rgba(255,255,255,0.06)", minHeight: 3 }} />
                    <div className="text-[9px] text-zinc-400 truncate max-w-full">{m.month_label}</div>
                  </div>
                );
              })}
            </div>
          );
        })() : (
          <div className="flex items-center gap-2 py-3 text-xs text-zinc-400">
            <Users className="w-4 h-4" />
            Sin usuarios registrados
          </div>
        )}
      </div>{/* Bottom row: recent users + recent payments + pending picks + parlays */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {/* Recent users */}
        <div className="surface rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-display font-bold">Últimos usuarios</div>
            <Link to="/admin/usuarios" className="text-xs text-yellow-400 hover:text-yellow-300 flex items-center gap-1">
              Ver todos <CaretRight weight="bold" className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-3">
            {(stats?.recent_users || []).map(u => (
              <div key={u.id} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-500/20 to-yellow-600/20 flex items-center justify-center text-xs font-black text-yellow-400 flex-shrink-0">
                  {(u.name || "?")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{u.name}</div>
                  <div className="text-[10px] text-zinc-500 truncate">{u.email}</div>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded border bg-white/[0.03] border-white/[0.06] text-zinc-400 uppercase tracking-wider font-bold flex-shrink-0">
                  {PLAN_LABELS[u.plan] || u.plan}
                </span>
              </div>
            ))}
            {!stats?.recent_users?.length && <div className="text-sm text-zinc-400 text-center py-4">Sin usuarios aún</div>}
          </div>
        </div>

        {/* Recent payments */}
        <div className="surface rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-display font-bold">Últimos pagos</div>
            <div className="flex items-center gap-3">
              <Link to="/admin/metodos-pago" className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                Métodos <CaretRight weight="bold" className="w-3 h-3" />
              </Link>
              <Link to="/admin/pagos" className="text-xs text-yellow-400 hover:text-yellow-300 flex items-center gap-1">
                Ver todos <CaretRight weight="bold" className="w-3 h-3" />
              </Link>
            </div>
          </div>
          <div className="space-y-3">
            {(stats?.recent_payments || []).map((p, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <CurrencyDollar weight="fill" className="w-4 h-4 text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{p.user_name}</div>
                  <div className="text-[10px] text-zinc-500">{p.created_at ? p.created_at.slice(0,10) : "—"} · {PLAN_LABELS[p.plan_id] || p.plan_id}</div>
                </div>
                <span className="text-sm font-black text-emerald-400">${p.amount}</span>
              </div>
            ))}
            {!stats?.recent_payments?.length && <div className="text-sm text-zinc-400 text-center py-4">Sin pagos aún</div>}
          </div>
        </div>

        {/* Pending picks */}
        <div className="surface rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-display font-bold">Picks pendientes</div>
            <Link to="/admin/analisis" className="text-xs text-yellow-400 hover:text-yellow-300 flex items-center gap-1">
              Gestionar <CaretRight weight="bold" className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-3">
            {(stats?.pending_picks || []).map(p => (
              <div key={p.id} className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-yellow-400 flex-shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{p.home_team} vs {p.away_team}</div>
                  <div className="text-[10px] text-zinc-500">{p.match_date ? p.match_date.slice(0,10) : "—"}</div>
                </div>
              </div>
            ))}
            {!stats?.pending_picks?.length && (
              <div className="text-sm text-zinc-400 text-center py-4">Sin picks pendientes</div>
            )}
          </div>
        </div>

        {/* Parlays quick link */}
        <div className="surface rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-display font-bold">Parlays Premium</div>
            <Link to="/admin/parlays" className="text-xs text-yellow-400 hover:text-yellow-300 flex items-center gap-1" data-testid="admin-panel-parlays-link">
              Gestionar <CaretRight weight="bold" className="w-3 h-3" />
            </Link>
          </div>
          <Link to="/admin/parlays/nuevo" className="btn-primary w-full text-sm" data-testid="admin-panel-create-parlay-btn">
            <span>+ Crear Parlay</span>
          </Link>
        </div>
      </div>
        </div>
      )}
      {tab === "comunicacion" && (
        <div className="space-y-6">
          <div className="text-sm text-zinc-400">Herramientas de uso diario — envía push y correos a tu comunidad sin salir de aquí.</div>
<div className="rounded-2xl border overflow-hidden" style={{ borderColor: "rgba(59,130,246,0.18)", background: "rgba(59,130,246,0.03)" }}>
<div className="flex items-center px-5 py-4">
  <div className="flex items-center gap-3">
    <div className="w-9 h-9 rounded-xl bg-blue-500/15 flex items-center justify-center flex-shrink-0">
      <Bell weight="fill" className="w-5 h-5 text-blue-400" />
    </div>
    <div className="text-left">
      <div className="text-[10px] text-blue-400 tracking-[0.25em] uppercase font-bold">Comunicación</div>
      <div className="text-sm font-semibold text-white">Enviar Notificación Push</div>
    </div>
  </div>
</div>
<div className="border-t px-5 pb-5 pt-4 space-y-3" style={{ borderColor: "rgba(59,130,246,0.12)" }}>
            <div>
              <label className="block text-[10px] text-zinc-500 uppercase tracking-wider font-bold mb-1">Título</label>
              <input data-testid="push-broadcast-title" value={pushTitle} onChange={e => setPushTitle(e.target.value)}
                placeholder="Ej: Nueva jugada publicada" maxLength={80}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-blue-500/50 transition-colors" />
            </div>
            <div>
              <label className="block text-[10px] text-zinc-500 uppercase tracking-wider font-bold mb-1">Mensaje</label>
              <textarea data-testid="push-broadcast-message" value={pushMessage} onChange={e => setPushMessage(e.target.value)}
                placeholder="Ej: ¡Nueva jugada con 90% de confianza!" maxLength={200} rows={2}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-blue-500/50 transition-colors resize-none" />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex-1">
                <label className="block text-[10px] text-zinc-500 uppercase tracking-wider font-bold mb-1">URL destino</label>
                <input data-testid="push-broadcast-url" value={pushUrl} onChange={e => setPushUrl(e.target.value)}
                  placeholder="/jugadas"
                  className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-blue-500/50 transition-colors" />
              </div>
              <button data-testid="push-broadcast-send-btn" onClick={sendBroadcast}
                disabled={sending || !pushTitle.trim() || !pushMessage.trim()}
                className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-300 hover:bg-blue-500/30 transition-all text-sm font-bold whitespace-nowrap disabled:opacity-40 w-full sm:w-auto">
                <Bell className="w-4 h-4" />
                {sending ? "Enviando..." : "Enviar push"}
              </button>
            </div>
          </div>
</div>
<div className="rounded-2xl border overflow-hidden" style={{ borderColor: "rgba(16,185,129,0.2)", background: "rgba(16,185,129,0.02)" }}>
<div className="flex items-center px-5 py-4">
  <div className="flex items-center gap-3">
    <div className="w-9 h-9 rounded-xl bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
      <EnvelopeSimple weight="fill" className="w-5 h-5 text-emerald-400" />
    </div>
    <div className="text-left">
      <div className="text-[10px] text-emerald-400 tracking-[0.25em] uppercase font-bold">Reactivación</div>
      <div className="text-sm font-semibold text-white">Email Masivo a Usuarios</div>
      <div className="text-xs text-zinc-500">Envía un correo persuasivo con tus stats recientes</div>
    </div>
  </div>
</div>
<div className="border-t px-5 pb-5 pt-4 space-y-4" style={{ borderColor: "rgba(16,185,129,0.12)" }}>

            {/* Audience selector */}
            <div>
              <label className="block text-[10px] text-zinc-500 uppercase tracking-wider font-bold mb-2">
                Audiencia
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { k: "inactive", l: "No renovaron", sub: "Suscripción vencida" },
                  { k: "trial",    l: "Trial no pagaron", sub: "Usaron día gratis" },
                  { k: "never",    l: "Nunca pagaron", sub: "Cuentas free" },
                  { k: "all",      l: "Todos", sub: "Toda la base" },
                ].map(a => (
                  <button
                    key={a.k}
                    onClick={() => { setEmailAudience(a.k); setEmailPreview(null); }}
                    data-testid={`email-audience-${a.k}`}
                    className={`p-3 rounded-xl text-left transition-all border ${
                      emailAudience === a.k
                        ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                        : "bg-white/[0.03] border-white/[0.06] text-zinc-400 hover:border-white/10"
                    }`}
                  >
                    <div className="text-xs font-bold">{a.l}</div>
                    <div className="text-[10px] mt-0.5 opacity-70">{a.sub}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Stats preview */}
            {emailPreview && (
              <div className="rounded-xl p-3 space-y-2" style={{ background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.2)" }}
                data-testid="email-preview-stats">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-400">Destinatarios:</span>
                  <span className="text-sm font-black text-emerald-400">{emailPreview.target_count} usuarios</span>
                </div>
                {emailPreview.stats && (
                  <div className="flex items-center gap-4 text-xs text-zinc-500">
                    <span>Ganadas 7d: <strong className="text-white">{emailPreview.stats.won_7d}</strong></span>
                    <span>Win Rate: <strong className="text-yellow-400">{emailPreview.stats.win_rate}%</strong></span>
                    <span>Profit: <strong className={emailPreview.stats.profit_7d >= 0 ? "text-emerald-400" : "text-red-400"}>
                      {emailPreview.stats.profit_7d >= 0 ? "+" : ""}{emailPreview.stats.profit_7d}u
                    </strong></span>
                  </div>
                )}
              </div>
            )}

            {/* Subject (optional) */}
            <div>
              <label className="block text-[10px] text-zinc-500 uppercase tracking-wider font-bold mb-1">
                Asunto <span className="text-zinc-400 normal-case">(opcional — se genera automático)</span>
              </label>
              <input
                data-testid="email-broadcast-subject"
                value={emailSubject}
                onChange={e => setEmailSubject(e.target.value)}
                placeholder="Ej: ¡Te extrañamos! 8 victorias esta semana 🏆"
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-emerald-500/50 transition-colors"
              />
            </div>

            {/* Custom message (optional) */}
            <div>
              <label className="block text-[10px] text-zinc-500 uppercase tracking-wider font-bold mb-1">
                Mensaje personalizado <span className="text-zinc-400 normal-case">(opcional)</span>
              </label>
              <textarea
                data-testid="email-broadcast-message"
                value={emailMessage}
                onChange={e => setEmailMessage(e.target.value)}
                placeholder="Ej: Esta semana cerramos 3 jugadas seguidas en Champions. ¡No te pierdas las de la próxima semana!"
                rows={3}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-emerald-500/50 transition-colors resize-none"
              />
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={previewEmailBroadcast}
                data-testid="email-preview-btn"
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-white/10 text-zinc-300 hover:bg-white/5 text-sm font-semibold transition-all flex-1"
              >
                <ArrowClockwise className="w-4 h-4" />
                Ver destinatarios
              </button>
              <button
                onClick={sendEmailBroadcast}
                disabled={emailSending || !emailPreview || emailPreview.target_count === 0}
                data-testid="email-broadcast-send-btn"
                className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all flex-1 disabled:opacity-40"
                style={{ background: "rgba(16,185,129,0.2)", border: "1px solid rgba(16,185,129,0.35)", color: "#6EE7B7" }}
              >
                <EnvelopeSimple weight="fill" className="w-4 h-4" />
                {emailSending
                  ? "Enviando..."
                  : emailPreview
                    ? `Enviar a ${emailPreview.target_count} usuarios`
                    : "Enviar email masivo"
                }
              </button>
            </div>
          </div>
</div>
        </div>
      )}
      {tab === "herramientas" && (
        <div className="space-y-6">
{/* Free Trials Control — visible toggle */}
      <div data-testid="trials-toggle-card"
           className={`flex items-center justify-between gap-4 p-4 rounded-xl border transition-colors
             ${trialsEnabled
               ? "border-emerald-500/25 bg-emerald-500/8"
               : "border-orange-500/35 bg-orange-500/10"}`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${trialsEnabled ? "bg-emerald-500/15" : "bg-orange-500/20"}`}>
            <Gift weight="duotone" className={`w-5 h-5 ${trialsEnabled ? "text-emerald-400" : "text-orange-400"}`} />
          </div>
          <div>
            <div className={`font-bold text-sm ${trialsEnabled ? "text-emerald-300" : "text-orange-300"}`}>
              Free Trials: {trialsEnabled ? "ACTIVOS" : "PAUSADOS"}
            </div>
            <div className="text-xs text-zinc-400 mt-0.5">
              {trialsEnabled
                ? "Los nuevos registros reciben días gratis. Filtro anti-VPN/IP duplicado activo."
                : "Los nuevos registros NO recibirán días gratis. Deberán pagar para acceder."}
            </div>
          </div>
        </div>
        <button onClick={() => setConfirmToggle("trials")} disabled={togglingTrials}
          data-testid="trials-toggle-btn"
          className={`flex items-center gap-2 text-xs px-4 py-2 rounded-lg border transition-all disabled:opacity-50 whitespace-nowrap font-bold
            ${trialsEnabled
              ? "bg-orange-500/15 border-orange-500/30 text-orange-300 hover:bg-orange-500/25"
              : "bg-emerald-500/15 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25"}`}>
          <Power className="w-3.5 h-3.5" />
          {togglingTrials ? "..." : (trialsEnabled ? "Pausar Trials" : "Activar Trials")}
        </button>
      </div>{/* Require Card on Trial Toggle */}
      <div data-testid="require-card-toggle-card"
           className={`flex items-center justify-between gap-4 p-4 rounded-xl border transition-colors
             ${requireCard
               ? "border-yellow-500/35 bg-yellow-500/10"
               : "border-zinc-700/40 bg-white/[0.02]"}`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${requireCard ? "bg-yellow-500/20" : "bg-zinc-700/30"}`}>
            <CurrencyDollar weight="duotone" className={`w-5 h-5 ${requireCard ? "text-yellow-400" : "text-zinc-400"}`} />
          </div>
          <div>
            <div className={`font-bold text-sm ${requireCard ? "text-yellow-300" : "text-zinc-300"}`}>
              Tarjeta requerida en trial: {requireCard ? "ACTIVADA" : "DESACTIVADA"}
            </div>
            <div className="text-xs text-zinc-400 mt-0.5">
              {requireCard
                ? "El trial exige tarjeta. Tras 24 horas, cobro automático $50/sem (cancela cuando quieran)."
                : "El trial NO exige tarjeta. Solo filtros de IP y toggle global aplican."}
            </div>
          </div>
        </div>
        <button onClick={() => setConfirmToggle("requireCard")} disabled={togglingRequireCard}
          data-testid="require-card-toggle-btn"
          className={`flex items-center gap-2 text-xs px-4 py-2 rounded-lg border transition-all disabled:opacity-50 whitespace-nowrap font-bold
            ${requireCard
              ? "bg-zinc-700/30 border-zinc-700/50 text-zinc-300 hover:bg-zinc-700/50"
              : "bg-yellow-500/20 border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/30"}`}>
          <CreditCard className="w-3.5 h-3.5" />
          {togglingRequireCard ? "..." : (requireCard ? "Quitar tarjeta" : "Exigir tarjeta")}
        </button>
      </div><div className="rounded-2xl border overflow-hidden" style={{ borderColor: "rgba(212,175,55,0.2)", background: "rgba(212,175,55,0.03)" }}>
<div className="flex items-center px-5 py-4">
  <div className="flex items-center gap-3">
    <div className="w-9 h-9 rounded-xl bg-yellow-500/15 flex items-center justify-center flex-shrink-0">
      <CreditCard weight="duotone" className="w-5 h-5 text-yellow-400" />
    </div>
    <div className="text-left">
      <div className="text-[10px] text-yellow-400 tracking-[0.25em] uppercase font-bold">Stripe</div>
      <div className="text-sm font-semibold text-white">Herramientas de activación y pagos</div>
      <div className="text-xs text-zinc-500">Activar usuarios, buscar por email, importar pagos históricos</div>
    </div>
  </div>
</div>
<div className="border-t space-y-4 p-4" style={{ borderColor: "rgba(212,175,55,0.1)" }}>
            {/* Safety Net */}
            <PendingActivationsCard />
            {/* Lookup */}
            <StripeLookupCard />
            {/* Import histórico */}
            <div className="border border-blue-500/20 rounded-xl p-4" style={{ background: "rgba(59,130,246,0.04)" }}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase font-bold mb-1" style={{ color: "#60A5FA" }}>Stripe Sync</div>
                  <div className="text-sm font-semibold text-white">Importar pagos históricos (últimos 90 días)</div>
                  <div className="text-xs text-zinc-500 mt-0.5">Útil si la lista de pagos estaba vacía por webhooks no guardados. No duplica.</div>
                </div>
                <button data-testid="stripe-import-btn"
                  onClick={async () => {
                    if (!window.confirm("¿Importar pagos de Stripe (últimos 90 días)?")) return;
                    try {
                      const r = await apiClient.post("/admin/stripe/import-payments?days=90");
                      toast.success(`✅ ${r.data.imported} importados, ${r.data.updated} actualizados, ${r.data.skipped} omitidos`);
                    } catch (err) { toast.error(err?.response?.data?.detail || "Error en importación"); }
                  }}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all text-sm font-semibold whitespace-nowrap"
                  style={{ borderColor: "rgba(59,130,246,0.3)", background: "rgba(59,130,246,0.1)", color: "#60A5FA" }}>
                  <Lightning className="w-4 h-4" /> Importar de Stripe
                </button>
              </div>
            </div>
          </div>
</div>
<div className="rounded-2xl border overflow-hidden" style={{ borderColor: "rgba(249,115,22,0.18)", background: "rgba(249,115,22,0.02)" }}>
<div className="flex items-center px-5 py-4">
  <div className="flex items-center gap-3">
    <div className="w-9 h-9 rounded-xl bg-orange-500/15 flex items-center justify-center flex-shrink-0">
      <Wrench weight="duotone" className="w-5 h-5 text-orange-400" />
    </div>
    <div className="text-left">
      <div className="text-[10px] text-orange-400 tracking-[0.25em] uppercase font-bold">Mantenimiento</div>
      <div className="text-sm font-semibold text-white">Herramientas de limpieza y datos</div>
    </div>
  </div>
</div>
<div className="border-t space-y-3 p-4" style={{ borderColor: "rgba(249,115,22,0.1)" }}>
            {/* Cleanup duplicates */}
            <div className="border border-orange-500/20 rounded-xl p-4" style={{ background: "rgba(249,115,22,0.04)" }}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase font-bold mb-1" style={{ color: "#FB923C" }}>Limpieza</div>
                  <div className="text-sm font-semibold text-white">Eliminar comprobantes manuales duplicados</div>
                  <div className="text-xs text-zinc-500 mt-0.5">Borra rechazados y consolida pendientes (1 por usuario). Aprobados intactos.</div>
                </div>
                <button data-testid="cleanup-duplicates-btn"
                  onClick={async () => {
                    if (!window.confirm("¿Limpiar duplicados de pagos manuales? Aprobados quedan intactos.")) return;
                    try {
                      const r = await apiClient.post("/admin/payments/manual/cleanup-duplicates");
                      toast.success(`✅ ${r.data.rejected_deleted} rechazados borrados. ${r.data.pendientes_actuales} pendientes.`);
                    } catch (err) { toast.error(err?.response?.data?.detail || "Error en limpieza"); }
                  }}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all text-sm font-semibold whitespace-nowrap"
                  style={{ borderColor: "rgba(249,115,22,0.3)", background: "rgba(249,115,22,0.1)", color: "#FB923C" }}>
                  🧹 Limpiar duplicados
                </button>
              </div>
            </div>
            {/* MLB Backfill */}
            <div className="border border-yellow-500/20 rounded-xl p-4" style={{ background: "rgba(212,175,55,0.04)" }}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase font-bold mb-1" style={{ color: "#D4AF37" }}>MLB Stats API</div>
                  <div className="text-sm font-semibold text-white">Rellenar pitchers en picks viejos</div>
                  <div className="text-xs text-zinc-500 mt-0.5">Completa picks MLB sin pitcher. Gratis, sin límites de API.</div>
                </div>
                <button data-testid="mlb-backfill-btn"
                  onClick={async () => {
                    if (!window.confirm("¿Ejecutar backfill de pitchers MLB? Puede tomar 1-2 minutos.")) return;
                    try {
                      const r = await apiClient.post("/admin/mlb/backfill-pitchers");
                      toast.success(`✅ Backfill: ${r.data.rellenados} rellenados, ${r.data.omitidos} omitidos`);
                    } catch (err) { toast.error(err?.response?.data?.detail || "Error en backfill"); }
                  }}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all text-sm font-semibold whitespace-nowrap"
                  style={{ borderColor: "rgba(212,175,55,0.3)", background: "rgba(212,175,55,0.1)", color: "#D4AF37" }}>
                  ⚾ Rellenar pitchers
                </button>
              </div>
            </div>
            {/* Danger Zone */}
            <div className="border border-red-500/20 rounded-xl p-4 bg-red-500/[0.03]">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <div className="text-[10px] text-red-400 tracking-[0.25em] uppercase font-bold mb-1">Zona peligrosa</div>
                  <div className="text-sm font-semibold text-white">Limpiar datos de prueba</div>
                  <div className="text-xs text-zinc-500 mt-0.5">Elimina usuarios/picks con "test" en su nombre. No toca cuentas reales ni admins.</div>
                </div>
                <button data-testid="cleanup-test-data-btn" onClick={() => setShowCleanupConfirm(true)} disabled={cleaning}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all text-sm font-semibold whitespace-nowrap disabled:opacity-50">
                  <Trash className="w-4 h-4" />
                  {cleaning ? "Limpiando..." : "Limpiar datos de prueba"}
                </button>
              </div>
            </div>
          </div>
</div>
        </div>
      )}

      {confirmToggle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}>
          <div className="surface rounded-2xl p-6 max-w-sm w-full border border-yellow-500/25 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-yellow-500/15 flex items-center justify-center flex-shrink-0">
                <Warning weight="fill" className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <div className="font-bold text-white mb-1">¿Cambiar configuración crítica?</div>
                <div className="text-sm text-zinc-400">
                  {confirmToggle === "trials"
                    ? (trialsEnabled ? "Se PAUSARÁN los Free Trials. Los nuevos registros no recibirán días gratis." : "Se ACTIVARÁN los Free Trials. Los nuevos registros recibirán días gratis.")
                    : (requireCard ? "Se QUITARÁ el requisito de tarjeta en el trial." : "Se EXIGIRÁ tarjeta en el trial. Tras 24h se cobrará $50/sem automáticamente.")}
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setConfirmToggle(null)} className="flex-1 py-2.5 rounded-lg border border-white/10 text-zinc-400 hover:text-white hover:border-white/20 transition-all text-sm font-semibold" data-testid="toggle-confirm-cancel">Cancelar</button>
              <button onClick={() => confirmToggle === "trials" ? toggleTrials() : toggleRequireCard()} className="flex-1 py-2.5 rounded-lg text-sm font-bold transition-all" style={{ background: "var(--gold)", color: "#000" }} data-testid="toggle-confirm-ok">Sí, cambiar</button>
            </div>
          </div>
        </div>
      )}
{/* ── Confirm Modal ─────────────────────────────────────────────── */}
      {showCleanupConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}>
          <div className="surface rounded-2xl p-6 max-w-sm w-full border border-red-500/25 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-500/15 flex items-center justify-center flex-shrink-0">
                <Warning weight="fill" className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <div className="font-bold text-white mb-1">¿Confirmar limpieza?</div>
                <div className="text-sm text-zinc-400">
                  Esto eliminará permanentemente:
                </div>
              </div>
            </div>
            <ul className="text-xs text-zinc-400 space-y-1.5 mb-5 ml-13 pl-1 border-l border-zinc-700 ml-4 pl-3">
              <li>· Usuarios cuyo email/nombre contenga <span className="text-red-300 font-mono">"test"</span></li>
              <li>· Picks con título/partido que contenga <span className="text-red-300 font-mono">"test"</span> o <span className="text-red-300 font-mono">"prueba"</span></li>
              <li>· Transacciones con ID que contenga <span className="text-red-300 font-mono">"test"</span></li>
            </ul>
            <div className="text-xs text-emerald-400 mb-5 flex items-center gap-1.5">
              <CheckCircle weight="fill" className="w-3.5 h-3.5" />
              Cuentas admin y usuarios reales <strong>no se tocan</strong>
            </div>
            <div className="flex gap-3">
              <button
                data-testid="cleanup-cancel-btn"
                onClick={() => { setShowCleanupConfirm(false); setCleanupArmed(false); }}
                className="flex-1 py-2.5 rounded-lg border border-white/10 text-zinc-400 hover:text-white hover:border-white/20 transition-all text-sm font-semibold"
              >
                Cancelar
              </button>
              <button
                data-testid="cleanup-confirm-btn"
                onClick={() => {
                  if (!cleanupArmed) { setCleanupArmed(true); return; }
                  setCleanupArmed(false);
                  runCleanup();
                }}
                className={`flex-1 py-2.5 rounded-lg border text-sm font-bold transition-all ${
                  cleanupArmed
                    ? "bg-red-500/40 border-red-400/60 text-white"
                    : "bg-red-500/20 border-red-500/30 text-red-300 hover:bg-red-500/30"
                }`}
              >
                {cleanupArmed ? "¿Seguro? Toca de nuevo" : "Sí, limpiar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Report Modal ──────────────────────────────────────────────── */}
      {cleanupReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}>
          <div className="surface rounded-2xl p-6 max-w-sm w-full border border-white/10 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-emerald-500/15 flex items-center justify-center">
                  <CheckCircle weight="fill" className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="font-bold text-white">Limpieza completada</div>
              </div>
              <button onClick={() => setCleanupReport(null)} className="text-zinc-500 hover:text-white transition-colors" data-testid="cleanup-report-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3 mb-5">
              {[
                { label: "Usuarios eliminados",       value: cleanupReport.users_deleted },
                { label: "Picks eliminados",          value: cleanupReport.picks_deleted },
                { label: "Transacciones eliminadas",  value: cleanupReport.transactions_deleted },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                  <span className="text-sm text-zinc-400">{label}</span>
                  <span className={`text-lg font-black ${value > 0 ? "text-red-400" : "text-zinc-500"}`}>{value}</span>
                </div>
              ))}
              <div className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                <span className="text-sm font-bold text-yellow-300">Total eliminado</span>
                <span className="text-xl font-black text-yellow-400">{cleanupReport.total_deleted}</span>
              </div>
            </div>
            <button
              data-testid="cleanup-report-done-btn"
              onClick={() => setCleanupReport(null)}
              className="w-full py-2.5 rounded-lg btn-primary text-sm"
            >
              Listo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
